import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles/AllStoresSummary.css';

const PAGE_SIZES = [25, 50, 100, 200, 500];

// Store-level fields shown in the subtitle (per-store data we pull).
const STORE_FIELDS = ['company', 'store', 'storeName', 'country', 'region', 'city', 'labelCount', 'gatewayCount', 'articleCount'];

// Numeric-sorted keys (right-aligned, numeric compare).
const NUMERIC = new Set(['labelCount', 'gatewayCount', 'articleCount', '_storeCount']);

// Column header labels + which row key they sort by (underscore keys are derived).
const COLUMNS = [
    { key: 'company', label: 'Company' },      // code only; company name stays hidden but searchable
    { key: '_status', label: 'Status' },
    { key: 'store', label: 'Store' },
    { key: 'storeName', label: 'Store Name' },
    { key: '_storeCount', label: 'Stores', num: true }, // account store count (expired rows only)
    { key: 'country', label: 'Country' },
    { key: 'region', label: 'Region' },
    { key: 'city', label: 'City' },
    { key: 'labelCount', label: 'Labels', num: true },
    { key: 'gatewayCount', label: 'Gateways', num: true },
    { key: 'articleCount', label: 'Articles', num: true },
    { key: '_expiry', label: 'Expiry' },
];

// Fields written to the .txt / .csv exports (store fields + status/expiry/store count).
const EXPORT_FIELDS = ['company', 'store', 'storeName', 'country', 'region', 'city', 'labelCount', 'gatewayCount', 'articleCount', 'status', 'expiry', 'storeCount'];

const isExpired = (r) => r._status === 'Expired';

// Resolve an export field for a given row (handles derived/underscore fields).
const exportVal = (s, f, companyCode) => {
    switch (f) {
        case 'company': return s.company || companyCode || '';
        case 'status': return s._status || 'Active';
        case 'expiry': return s._expiry || '';
        case 'storeCount': return isExpired(s) ? (s._storeCount ?? '') : '';
        default: return s[f];
    }
};

// Flatten a value to a single-line, comma-safe cell (matches the .txt export rules).
const cell = (v) =>
    (v === undefined || v === null ? '' : String(v)).replace(/[\r\n]+/g, ' ').replace(/,/g, ';').trim();

const download = (filename, text, mime = 'text/plain;charset=utf-8;') => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const todayStamp = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const AllStoresSummary = () => {
    const [running, setRunning] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [companies, setCompanies] = useState([]); // [{ code, name, stores: [...] }]
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState({ column: 'company', direction: 'asc' });
    const [page, setPage] = useState(0);          // 0-indexed
    const [pageSize, setPageSize] = useState(100);
    const [hideExpired, setHideExpired] = useState(false); // also excludes expired from exports
    const [showDiscrepancy, setShowDiscrepancy] = useState(false);
    const esRef = useRef(null);

    // Tear down the stream if the component unmounts mid-run.
    useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);

    const start = () => {
        if (running) return;
        setRunning(true);
        setDone(false);
        setError(null);
        setCompanies([]);
        setPage(0);
        setProgress({ current: 0, total: 0 });

        const es = new EventSource('/api/global-action/actions/all-companies-stores-summary');
        esRef.current = es;

        es.addEventListener('meta', (e) => {
            const { totalCompanies } = JSON.parse(e.data);
            setProgress({ current: 0, total: totalCompanies });
        });

        es.addEventListener('company', (e) => {
            const c = JSON.parse(e.data);
            setCompanies((prev) => [...prev, { code: c.code, name: c.name, isActive: c.isActive, rollup: c.rollup || null, stores: c.stores || [] }]);
            setProgress({ current: c.index, total: c.total });
        });

        es.addEventListener('complete', () => {
            setRunning(false);
            setDone(true);
            es.close();
            esRef.current = null;
        });

        es.addEventListener('error', (e) => {
            let msg = 'Stream error';
            try { if (e.data) msg = JSON.parse(e.data).message || msg; } catch { /* native EventSource error has no data */ }
            setError(msg);
            setRunning(false);
            es.close();
            esRef.current = null;
        });
    };

    const stop = () => {
        if (esRef.current) { esRef.current.close(); esRef.current = null; }
        setRunning(false);
    };

    const totals = useMemo(() => {
        let activeStores = 0, labels = 0, gateways = 0, articles = 0;
        let expiredCompanies = 0, expiredStores = 0, expiredLabels = 0, expiredGateways = 0, expiredArticles = 0;
        for (const c of companies) {
            for (const s of c.stores) {
                if (s._status === 'Expired') {
                    expiredCompanies += 1;
                    expiredStores += Number(s._storeCount) || 0;
                    expiredLabels += Number(s.labelCount) || 0;
                    expiredGateways += Number(s.gatewayCount) || 0;
                    expiredArticles += Number(s.articleCount) || 0;
                } else {
                    activeStores += 1;
                    labels += Number(s.labelCount) || 0;
                    gateways += Number(s.gatewayCount) || 0;
                    articles += Number(s.articleCount) || 0;
                }
            }
        }
        return {
            companies: companies.length,
            activeStores, labels, gateways, articles,
            expiredCompanies, expiredStores, expiredLabels, expiredGateways, expiredArticles
        };
    }, [companies]);

    // Reconciliation: our live per-store pull vs SaaS's company-level rollup (the dashboard).
    const discrepancy = useMemo(() => {
        let aLiveStores = 0, aLiveLabels = 0, aLiveArts = 0, aLiveGw = 0;
        let aRollStores = 0, aRollLabels = 0, aRollArts = 0, aRollGw = 0;
        let eStores = 0, eLabels = 0, eArts = 0, eGw = 0, eAccts = 0;
        let bucketA = 0, bucketB = 0, hasRollup = false;
        const excluded = [];

        for (const c of companies) {
            const r = c.rollup;
            if (r) hasRollup = true;
            if (c.isActive === false) {
                eAccts += 1;
                eStores += r ? r.stores : (Number(c.stores?.[0]?._storeCount) || 0);
                eLabels += r ? r.labels : 0;
                eArts += r ? r.articles : 0;
                eGw += r ? r.gateways : 0;
                continue;
            }
            const stores = c.stores || [];
            const liveStores = stores.length;
            const liveLabels = stores.reduce((a, s) => a + (Number(s.labelCount) || 0), 0);
            const liveArts = stores.reduce((a, s) => a + (Number(s.articleCount) || 0), 0);
            const liveGw = stores.reduce((a, s) => a + (Number(s.gatewayCount) || 0), 0);
            aLiveStores += liveStores; aLiveLabels += liveLabels; aLiveArts += liveArts; aLiveGw += liveGw;
            if (!r) continue;
            aRollStores += r.stores; aRollLabels += r.labels; aRollArts += r.articles; aRollGw += r.gateways;
            const artDiff = liveArts - r.articles;
            const storeDiff = liveStores - r.stores;
            if (storeDiff > 0) {
                bucketA += artDiff;
                // Excluded store = the one whose product count best explains the gap.
                let best = null, bd = Infinity;
                for (const s of stores) {
                    const d = Math.abs((Number(s.articleCount) || 0) - artDiff);
                    if (d < bd) { bd = d; best = s; }
                }
                if (best) excluded.push({
                    company: c.code, companyName: c.name, store: best.store, storeName: best.storeName,
                    products: Number(best.articleCount) || 0, labels: Number(best.labelCount) || 0, gateways: Number(best.gatewayCount) || 0
                });
            } else if (artDiff !== 0) {
                bucketB += artDiff;
            }
        }
        excluded.sort((a, b) => b.products - a.products);
        return {
            hasRollup,
            active: { liveStores: aLiveStores, liveLabels: aLiveLabels, liveArts: aLiveArts, liveGw: aLiveGw, rollStores: aRollStores, rollLabels: aRollLabels, rollArts: aRollArts, rollGw: aRollGw },
            expired: { accts: eAccts, stores: eStores, labels: eLabels, arts: eArts, gw: eGw },
            dashboard: { stores: aRollStores + eStores, labels: aRollLabels + eLabels, arts: aRollArts + eArts, gw: aRollGw + eGw },
            bucketA, bucketB, excluded
        };
    }, [companies]);

    // Flatten every store into a single row list (company is a field on each row).
    const flatRows = useMemo(() => {
        const rows = [];
        for (const c of companies) {
            for (const s of c.stores) rows.push({ ...s, company: s.company || c.code, _companyName: c.name });
        }
        return rows;
    }, [companies]);

    // Rows visible in the table (hideExpired drops expired account rows).
    const baseRows = useMemo(
        () => (hideExpired ? flatRows.filter((r) => !isExpired(r)) : flatRows),
        [flatRows, hideExpired]
    );

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseRows;
        return baseRows.filter((r) =>
            (r.company || '').toLowerCase().includes(q) ||
            (r.store || '').toLowerCase().includes(q) ||
            (r.storeName || '').toLowerCase().includes(q) ||
            (r.city || '').toLowerCase().includes(q) ||
            (r.region || '').toLowerCase().includes(q) ||
            (r.country || '').toLowerCase().includes(q) ||
            (r._companyName || '').toLowerCase().includes(q) ||
            (r._status || '').toLowerCase().includes(q) ||
            (r._expiry || '').toLowerCase().includes(q)
        );
    }, [baseRows, search]);

    const sortedRows = useMemo(() => {
        const rows = [...filteredRows];
        const { column, direction } = sort;
        const dir = direction === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
            if (NUMERIC.has(column)) return ((Number(a[column]) || 0) - (Number(b[column]) || 0)) * dir;
            return String(a[column] ?? '').localeCompare(String(b[column] ?? '')) * dir;
        });
        return rows;
    }, [filteredRows, sort]);

    const total = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const pageRows = useMemo(
        () => sortedRows.slice(safePage * pageSize, safePage * pageSize + pageSize),
        [sortedRows, safePage, pageSize]
    );
    const firstRow = total === 0 ? 0 : safePage * pageSize + 1;
    const lastRow = Math.min(total, safePage * pageSize + pageSize);

    // Windowed page-number list: first, last, and ±2 around current, with … gaps.
    const pageNumbers = useMemo(() => {
        const last = totalPages - 1;
        const win = 2;
        const keep = new Set();
        for (let p = 0; p <= last; p++) {
            if (p === 0 || p === last || (p >= safePage - win && p <= safePage + win)) keep.add(p);
        }
        const sorted = [...keep].sort((a, b) => a - b);
        const out = [];
        let prev = -1;
        for (const p of sorted) {
            if (prev >= 0 && p - prev > 1) out.push(`gap-${p}`);
            out.push(p);
            prev = p;
        }
        return out;
    }, [safePage, totalPages]);

    const toggleSort = (column) =>
        setSort((prev) => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
        }));

    // companies filtered for export (hideExpired drops whole expired accounts).
    const exportCompanies = () =>
        hideExpired ? companies.filter((c) => !(c.stores || []).some((s) => s._status === 'Expired')) : companies;

    // Grouped .txt — one COMPANY block per account; expired accounts show as one line.
    const buildTxt = () => {
        const list = exportCompanies();
        let compCount = 0, storeCount = 0;
        const lines = [];
        lines.push('# SaaS store export — SOLUM SaaS (east)');
        lines.push(`# Fields: ${EXPORT_FIELDS.join(', ')}`);
        lines.push(`# (commas inside a value were replaced with ';'; expired accounts show account-level counts, storeCount = # stores)`);
        lines.push('');
        for (const c of list) {
            const expired = (c.stores || []).some((s) => s._status === 'Expired');
            compCount += 1;
            lines.push(`COMPANY: ${c.code}${c.name ? `  (${c.name})` : ''}${expired ? '  [EXPIRED]' : ''}`);
            lines.push('');
            for (const s of c.stores) {
                if (s._status === 'Expired') storeCount += Number(s._storeCount) || 0; else storeCount += 1;
                lines.push(EXPORT_FIELDS.map((f) => cell(exportVal(s, f, c.code))).join(', '));
            }
            lines.push('');
        }
        lines.push(`# TOTAL: ${compCount} companies, ${storeCount} stores${hideExpired ? ' (expired excluded)' : ''}`);
        return lines.join('\n');
    };

    // Flat CSV — one row per store (expired = one account row), quoted.
    const buildCsv = () => {
        const rows = [EXPORT_FIELDS.map((f) => `"${f}"`).join(',')];
        for (const c of exportCompanies()) {
            for (const s of c.stores) {
                rows.push(EXPORT_FIELDS.map((f) => `"${String(exportVal(s, f, c.code) ?? '').replace(/"/g, '""')}"`).join(','));
            }
        }
        return rows.join('\n');
    };

    const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;

    return (
        <div className="acss-container">
            <div className="acss-header">
                <div className="acss-title-block">
                    <h3>All Companies / All Stores Summary</h3>
                    <p className="acss-sub">
                        Pulls every company and every store from SOLUM SaaS (fields:&nbsp;
                        <code>{STORE_FIELDS.join(', ')}</code>). Expired accounts show as one account-level row.
                    </p>
                </div>
                <div className="acss-actions">
                    {!running && (
                        <button className="acss-btn acss-btn-primary" onClick={start}>
                            {done ? 'Regenerate' : 'Generate Summary'}
                        </button>
                    )}
                    {running && (
                        <button className="acss-btn acss-btn-stop" onClick={stop}>Stop</button>
                    )}
                    <button
                        className="acss-btn"
                        onClick={() => download(`all-companies-stores-summary-${todayStamp()}.txt`, buildTxt())}
                        disabled={!companies.length}
                        title={companies.length ? 'Download grouped .txt' : 'Run the summary first'}
                    >
                        Download .txt
                    </button>
                    <button
                        className="acss-btn"
                        onClick={() => download(`all-companies-stores-summary-${todayStamp()}.csv`, buildCsv(), 'text/csv;charset=utf-8;')}
                        disabled={!companies.length}
                        title={companies.length ? 'Download flat .csv' : 'Run the summary first'}
                    >
                        Download .csv
                    </button>
                    <button
                        className="acss-btn acss-btn-calc"
                        onClick={() => setShowDiscrepancy(true)}
                        disabled={!companies.length}
                        title={companies.length ? 'Reconcile our live pull vs the SaaS dashboard' : 'Run the summary first'}
                    >
                        📊 Calculate Discrepancy
                    </button>
                </div>
            </div>

            {(running || done || progress.total > 0) && (
                <div className="acss-progress">
                    <div className="acss-progress-bar">
                        <div className="acss-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="acss-progress-text">
                        {running ? 'Fetching' : done ? 'Done' : 'Ready'} — {progress.current}/{progress.total || '…'} companies ({pct}%)
                    </span>
                </div>
            )}

            {error && <div className="acss-error">Error: {error}</div>}

            {companies.length > 0 && (
                <div className="acss-stats">
                    <div className="acss-stat"><span className="acss-stat-num">{totals.companies.toLocaleString()}</span><span className="acss-stat-label">Companies</span></div>
                    <div className="acss-stat"><span className="acss-stat-num">{totals.activeStores.toLocaleString()}</span><span className="acss-stat-label">Active Stores</span></div>
                    <div className="acss-stat"><span className="acss-stat-num">{totals.labels.toLocaleString()}</span><span className="acss-stat-label">Active Labels</span></div>
                    <div className="acss-stat"><span className="acss-stat-num">{totals.gateways.toLocaleString()}</span><span className="acss-stat-label">Active Gateways</span></div>
                    <div className="acss-stat"><span className="acss-stat-num">{totals.articles.toLocaleString()}</span><span className="acss-stat-label">Active Products</span></div>
                    <div className="acss-stat acss-stat-expired"><span className="acss-stat-num">{totals.expiredCompanies.toLocaleString()}</span><span className="acss-stat-label">Expired Accts</span></div>
                    <div className="acss-stat acss-stat-expired"><span className="acss-stat-num">{totals.expiredStores.toLocaleString()}</span><span className="acss-stat-label">Expired Stores</span></div>
                    <div className="acss-stat acss-stat-expired"><span className="acss-stat-num">{totals.expiredLabels.toLocaleString()}</span><span className="acss-stat-label">Expired Labels</span></div>
                    <div className="acss-stat acss-stat-expired"><span className="acss-stat-num">{totals.expiredGateways.toLocaleString()}</span><span className="acss-stat-label">Expired Gateways</span></div>
                    <div className="acss-stat acss-stat-expired"><span className="acss-stat-num">{totals.expiredArticles.toLocaleString()}</span><span className="acss-stat-label">Expired Products</span></div>
                </div>
            )}

            {companies.length > 0 && (
                <div className="acss-toolbar">
                    <input
                        type="text"
                        className="acss-search"
                        placeholder="Filter by company, store, name, city…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    />
                    <div className="acss-toolbar-right">
                        <label className="acss-checkbox">
                            <input
                                type="checkbox"
                                checked={hideExpired}
                                onChange={(e) => { setHideExpired(e.target.checked); setPage(0); }}
                            />
                            Hide expired companies
                        </label>
                        <label className="acss-pagesize">
                            Rows per page:
                            <select
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                            >
                                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <span className="acss-toolbar-note">
                            {total.toLocaleString()} row{total === 1 ? '' : 's'}
                            {search ? ' (filtered)' : ''}
                        </span>
                    </div>
                </div>
            )}

            {companies.length > 0 && (
                <div className="acss-table-wrap">
                    <table className="acss-table">
                        <thead>
                            <tr>
                                {COLUMNS.map((col) => (
                                    <th
                                        key={col.key}
                                        className={`acss-sortable${col.num ? ' acss-num' : ''}`}
                                        onClick={() => toggleSort(col.key)}
                                    >
                                        {col.label}
                                        {sort.column === col.key && (sort.direction === 'asc' ? ' ▲' : ' ▼')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map((s, idx) => {
                                const expired = isExpired(s);
                                return (
                                    <tr key={`${s.company}:${s.store}:${idx}`} className={expired ? 'acss-row-expired' : ''}>
                                        <td>{s.company || ''}</td>
                                        <td>
                                            <span className={`acss-badge ${expired ? 'acss-badge-expired' : 'acss-badge-active'}`}>
                                                {expired ? 'Expired' : 'Active'}
                                            </span>
                                        </td>
                                        <td>{s.store || (expired ? '—' : '')}</td>
                                        <td>{s.storeName || ''}</td>
                                        <td className="acss-num">{expired ? (Number(s._storeCount) || 0) : '—'}</td>
                                        <td>{s.country || ''}</td>
                                        <td>{s.region || ''}</td>
                                        <td>{s.city || ''}</td>
                                        <td className="acss-num">{Number(s.labelCount) || 0}</td>
                                        <td className="acss-num">{Number(s.gatewayCount) || 0}</td>
                                        <td className="acss-num">{Number(s.articleCount) || 0}</td>
                                        <td>{s._expiry || '—'}</td>
                                    </tr>
                                );
                            })}
                            {pageRows.length === 0 && (
                                <tr><td colSpan={12} className="acss-empty">No rows match your filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {companies.length > 0 && (
                <div className="acss-pager">
                    <span className="acss-pager-info">
                        {firstRow.toLocaleString()}–{lastRow.toLocaleString()} of {total.toLocaleString()}
                    </span>
                    <div className="acss-pager-controls">
                        <button className="acss-btn acss-btn-sm" onClick={() => setPage(0)} disabled={safePage === 0}>« First</button>
                        <button className="acss-btn acss-btn-sm" onClick={() => setPage(safePage - 1)} disabled={safePage === 0}>‹ Prev</button>
                        {pageNumbers.map((p) =>
                            typeof p === 'number' ? (
                                <button
                                    key={p}
                                    className={`acss-btn acss-btn-sm acss-page-num${p === safePage ? ' acss-page-active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p + 1}
                                </button>
                            ) : (
                                <span key={p} className="acss-pager-ellipsis">…</span>
                            )
                        )}
                        <button className="acss-btn acss-btn-sm" onClick={() => setPage(safePage + 1)} disabled={safePage >= totalPages - 1}>Next ›</button>
                        <button className="acss-btn acss-btn-sm" onClick={() => setPage(totalPages - 1)} disabled={safePage >= totalPages - 1}>Last »</button>
                    </div>
                </div>
            )}

            {showDiscrepancy && (
                <div className="acss-modal-overlay" onClick={() => setShowDiscrepancy(false)}>
                    <div className="acss-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="acss-modal-head">
                            <h3>Discrepancy — our live pull vs SaaS dashboard</h3>
                            <button className="acss-modal-x" onClick={() => setShowDiscrepancy(false)}>×</button>
                        </div>

                        {!discrepancy.hasRollup ? (
                            <div className="acss-modal-body">
                                <p className="acss-error">
                                    Rollup counts aren't loaded. Restart the server and click <strong>Regenerate</strong>,
                                    then reopen this — the reconciliation needs SaaS's company-level counts.
                                </p>
                            </div>
                        ) : (() => {
                            const d = discrepancy;
                            const n = (v) => Number(v).toLocaleString();
                            const liveTotalArts = d.active.liveArts;              // our headline products (active)
                            const dashArts = d.dashboard.arts;                    // dashboard products (all accts)
                            return (
                                <div className="acss-modal-body">
                                    <p className="acss-modal-lead">
                                        Our live pull counts <strong>{n(liveTotalArts)}</strong> products;
                                        the SaaS dashboard shows <strong>{n(dashArts)}</strong>.
                                        Here's every step between them.
                                    </p>

                                    <h4>Reconciliation walk</h4>
                                    <div className="acss-modal-tablewrap">
                                        <table className="acss-modal-table">
                                            <thead>
                                                <tr><th>Step</th><th className="acss-num">Stores</th><th className="acss-num">Products</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Our live pull (active accounts)</td>
                                                    <td className="acss-num">{n(d.active.liveStores)}</td>
                                                    <td className="acss-num">{n(d.active.liveArts)}</td>
                                                </tr>
                                                <tr className="acss-modal-sub">
                                                    <td>− Excluded MASTER / test stores ({d.excluded.length})</td>
                                                    <td className="acss-num">−{n(d.active.liveStores - d.active.rollStores)}</td>
                                                    <td className="acss-num">−{n(d.bucketA)}</td>
                                                </tr>
                                                <tr className="acss-modal-sub">
                                                    <td>− Per-store differences (rounding)</td>
                                                    <td className="acss-num">0</td>
                                                    <td className="acss-num">−{n(d.bucketB)}</td>
                                                </tr>
                                                <tr className="acss-modal-total">
                                                    <td>= SaaS rollup (active accounts)</td>
                                                    <td className="acss-num">{n(d.active.rollStores)}</td>
                                                    <td className="acss-num">{n(d.active.rollArts)}</td>
                                                </tr>
                                                <tr className="acss-modal-sub acss-modal-expired">
                                                    <td>+ Expired accounts ({d.expired.accts})</td>
                                                    <td className="acss-num">+{n(d.expired.stores)}</td>
                                                    <td className="acss-num">+{n(d.expired.arts)}</td>
                                                </tr>
                                                <tr className="acss-modal-grand">
                                                    <td>= SaaS dashboard (all accounts)</td>
                                                    <td className="acss-num">{n(d.dashboard.stores)}</td>
                                                    <td className="acss-num">{n(d.dashboard.arts)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <h4>The {d.excluded.length} excluded stores <span className="acss-modal-note">(SaaS omits these from its counts; we include them)</span></h4>
                                    <div className="acss-modal-tablewrap acss-modal-scroll">
                                        <table className="acss-modal-table">
                                            <thead>
                                                <tr>
                                                    <th>Company</th><th>Store</th><th>Name</th>
                                                    <th className="acss-num">Products</th><th className="acss-num">Labels</th><th className="acss-num">Gateways</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {d.excluded.map((s, i) => (
                                                    <tr key={`${s.company}:${s.store}:${i}`}>
                                                        <td>{s.company}</td>
                                                        <td>{s.store}</td>
                                                        <td>{s.storeName}</td>
                                                        <td className="acss-num">{n(s.products)}</td>
                                                        <td className="acss-num">{n(s.labels)}</td>
                                                        <td className="acss-num">{n(s.gateways)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="acss-modal-total">
                                                    <td colSpan={3}>TOTAL — {d.excluded.length} stores</td>
                                                    <td className="acss-num">{n(d.excluded.reduce((a, s) => a + s.products, 0))}</td>
                                                    <td className="acss-num">{n(d.excluded.reduce((a, s) => a + s.labels, 0))}</td>
                                                    <td className="acss-num">{n(d.excluded.reduce((a, s) => a + s.gateways, 0))}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllStoresSummary;
